import * as React from 'react'

import { Alert, Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { sortBy } from 'lodash'

import Selector from 'modules/core/components/Selector'
import { useFormContext } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import { NEW_EVALUATION_FROM_SCRATCH } from 'modules/ats/components/EvaluationForm/components/EvaluationFormEditor'
import { EvaluationFormValues } from 'modules/ats/components/EvaluationForm/useEvaluationFormForm'
import { useGetEvaluationFormTemplates } from 'modules/ats/hooks/evaluationForms/useGetEvaluationFormTemplates'

interface Props {
  onSelectOption: (value: string | null) => void
}

export const AddEvaluationFormSelector = ({ onSelectOption }: Props) => {
  const form = useFormContext<EvaluationFormValues>()
  const [showEditor, setShowEditor] = React.useState(false)
  const [selectedValue, setSelectedValue] = React.useState<string | null>('')
  const { loading, evaluationFormTemplates } = useGetEvaluationFormTemplates()

  const templateOptions = sortBy(evaluationFormTemplates, (template) => template.name).map(
    (template) => {
      return {
        label: template.name,
        value: template.id.toString(),
      }
    }
  )

  let options = [
    {
      label: i18n.t('ats.job_posting_settings.evaluation_forms.create_from_scratch'),
      value: NEW_EVALUATION_FROM_SCRATCH,
    },
  ]

  if (!loading) {
    options = [...options, ...templateOptions]
  }

  const onChange = (value: string | null) => {
    setSelectedValue(value)
    setShowEditor(true)
    onSelectOption(value)
  }

  const showMessageWhenSelectTemplate = showEditor && !!form.values.basedOnId

  return (
    <Box gap="s16">
      {showMessageWhenSelectTemplate && (
        <Alert color="warning">
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Icon.Medium icon="alert-circle" color="grey800" />
            {i18n.t('ats.job_posting_settings.evaluation_forms.new_modal.from_template_warning')}
          </Box>
        </Alert>
      )}

      {!showEditor && (
        <Alert>{i18n.t('ats.job_posting_settings.evaluation_forms.new_modal.add_message')}</Alert>
      )}

      <Selector.Popover
        label={i18n.t('ats.job_posting_settings.evaluation_forms.selector_label')}
        searchPlaceholder={i18n.t('common.search')}
        placeholder={i18n.t('common.select_basic')}
        blankLabel={i18n.t('common.empty')}
        value={[selectedValue]}
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        onChange={(value) => onChange(value as string | null)}
        isDisabled={loading}
      >
        {options.map((option) => (
          <Selector.Option
            key={option.value}
            value={option.value}
            label={option.label}
            searchValue={option.label}
          >
            {option.value === NEW_EVALUATION_FROM_SCRATCH ? (
              <Box flexDirection="row" gap="s8" alignItems="center">
                <Icon.Medium icon="add" color="grey1000" />
                <Text.Normal size="200" color="grey1000">
                  {option.label}
                </Text.Normal>
              </Box>
            ) : (
              option.label
            )}
          </Selector.Option>
        ))}
      </Selector.Popover>
    </Box>
  )
}
